import refreshIcon from '../../../assets/images/icons/refresh-icon.svg'
import qrIcon from '../../../assets/images/icons/qr-icon.svg'
import saveIcon from '../../../assets/images/icons/download-icon.svg'
import infoIcon from '../../../assets/images/icons/info-outline.svg'
import checkIcon from '../../../assets/images/icons/checkGreen.svg'
import checkFilledIcon from '../../../assets/images/icons/checkFilledGreen.svg'
import errorIcon from '../../../assets/images/icons/errorOutline.svg'
import { PillButton } from '../../../components/buttons/PillButton'
import { Modal } from '../../../components/Modal'
import { useCallback, useContext, useRef, useState } from 'react'
import { SamplesContext, SamplesResponseType, UpdateSamplesContext } from '../../../context/samplesContext'
import { useNavigate } from 'react-router-dom'
import { PATH_INFO } from '../../utils/helper'
import { UserContext } from '../../../context/userContext'

export function ButtonContainer({ isUpdate, setIsUpdate }: { isUpdate: boolean, setIsUpdate: React.Dispatch<React.SetStateAction<boolean>> }) {
  const { isAuth } = useContext(UserContext)
  const { sample, rowSample, canvasImage } = useContext(SamplesContext)
  const { saveSample, updateRowSample } = useContext(UpdateSamplesContext)
  const navigate = useNavigate()

  const [saveResult, setSaveResult] = useState<[boolean, number]>([false, 0])

  const [isSaveResultModalOpen, setIsSaveResultModalOpen] = useState(false)
  const closeSaveResultModal = useCallback(() => { setIsSaveResultModalOpen(false) }, [])
  const [isBlankSampleModal, setIsBlankSampleModal] = useState(false)
  const closeBlankSampleModal = useCallback(() => { setIsBlankSampleModal(false) }, [])

  const downloadPdf = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/export/measurement-report`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ sample_id: rowSample.id, url: rowSample.url, sampleImage: canvasImage })
      })
      if (res.status === 401 || res.status === 403) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (res.ok) {
        const blob = await res.blob()
        const downloadUrl = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        document.body.appendChild(a)
        a.href = downloadUrl

        a.download = 'measurement_report.pdf'

        a.click()
        a.remove()
        window.URL.revokeObjectURL(downloadUrl)
      }
    } catch (error) {
      console.error("Error PDF download.");
    }
  }, [navigate, rowSample, canvasImage])

  const onSaveSample = useCallback(async () => {
    if (!isAuth) return
    const newSample = { ...rowSample }
    const newMeasurements = newSample.sampleMeasurements.filter((m) => !m.is_deleted)
    const hasBlankSample = newMeasurements.filter((m) => !m.measure_name)
    if (hasBlankSample.length > 0) {
      setIsBlankSampleModal(true)
      return
    }
    const result = await saveSample({ ...newSample, sampleMeasurements: newMeasurements }, isUpdate)
    setSaveResult(result)
    setIsSaveResultModalOpen(true)

    if (result[0]) downloadPdf()
  }, [saveSample, downloadPdf, rowSample, isUpdate, isAuth])

  return (
    <div className="w-full">
      <div className="flex flex-col space-y-[10px] mt-[5px] desktop-xmd:mt-[15px]">
        <div className='flex space-x-[5px] desktop-xmd:contents desktop-xmd:space-x-0 desktop-xmd:space-y-[10px]'>
          <PillButton
            label='CLEAR'
            iconSrc={refreshIcon}
            toColor='#D9FFC7'
            fromColor='#278B25'
            onClickHandle={() => {
              updateRowSample(sample)
              setIsUpdate(false)
            }}
            isAuth={isAuth}
          />
          <PillButton
            label='url/QR 検索'
            iconSrc={qrIcon}
            toColor='#F9CEFF'
            fromColor='#A344BC'
            onClickHandle={() => {
              navigate(PATH_INFO.QR_SEARCH.PATH)
            }}
            isAuth={isAuth}
          />
        </div>
        <PillButton
          label='SAVE'
          iconSrc={saveIcon}
          toColor='#7FC1FF'
          fromColor='#2300FF'
          onClickHandle={onSaveSample}
          isAuth={isAuth}
        />
      </div>
      <SaveResultModal isOpen={isSaveResultModalOpen} onClose={closeSaveResultModal} saveResult={saveResult} sample={sample} />
      <BlankSampleModal isOpen={isBlankSampleModal} onClose={closeBlankSampleModal} />
    </div>
  )
}

function SaveResultModal({ isOpen, onClose, saveResult, sample }: { isOpen: boolean, onClose: () => void, saveResult: [boolean, number], sample: SamplesResponseType }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const openModal = useCallback(() => { setIsModalOpen(true) }, [])
  const closeModal = useCallback(() => { setIsModalOpen(false) }, [])
  const inputRef = useRef<HTMLInputElement>(null)

  const onCopyUrl = useCallback(async () => {
    if (inputRef.current) {
      inputRef.current.select()
      try {
        await navigator.clipboard.writeText(inputRef.current.value)
      } catch (error) {
        console.error('リンクのコピーに失敗しました。')
      }
      openModal()
    }
  }, [openModal])

  const onQrDownload = useCallback(async (id: string) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/${id}/qr-code/download`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      })

      if (!res.ok) throw new Error('ダウンロード失敗')

      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `qr_${id}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)

      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.log('DOWNLOAD ERROR')
    }
  }, [])

  return (
    <>
      {saveResult[0] ? (
        <Modal isOpen={isOpen} onClose={onClose} className='flex flex-col items-center w-[485px] h-[745px] pt-5 px-5' >
          <div className='flex items-center justify-center w-full space-x-[10px] mb-[20px] mt-[50px]'>
            <img src={checkFilledIcon} alt='check' className='w-[26px] h-[26px]' />
            <span className='mt-[-5px]'>saveしました。</span>
          </div>
          <div className='flex items-center w-full'>
            <div className='w-2 h-[35px] rounded-sm bg-[#3E3E3E] mr-[5px]' ></div>
            <span className='text-[#3E3E3E] text-[16px]'>
              QR CORD
            </span>
          </div>
          <div className='flex items-center justify-center w-[393px] h-[381px] border-[2.5px] border-[#D9D9D9] rounded-[20px] mt-[8px]'>
            <img src={sample.qr_code} alt='qrcode' className='w-[332px] h-[355px] rounded-[20px]' />
          </div>
          <div className='w-[445px] h-[40px] border-[2.5px] border-[#D9D9D9] rounded-[10px] mt-[8px]'>
            <input ref={inputRef} value={sample.url} className='w-full h-full px-[18px] rounded-[10px]' readOnly />
          </div>
          <div className='flex w-full justify-between items-center mt-[28px]'>
            <div className='relative group'>
              <span className={[
                'flex',
                'items-center',
                'justify-center',
                'w-[446px]',
                'h-[92px]',
                'px-[19px]',
                'absolute',
                'bg-white',
                'text-[14px]',
                '-translate-x-1/2',
                'left-1/2',
                'bottom-[-100px]',
                'opacity-0',
                'group-hover:opacity-100',
                'transition',
                "pointer-events-none"
              ].join(" ")} style={{ boxShadow: '0px 0px 4px 0px #00000040' }}>
                コードをスキャンするには、スマートフォンのQRスキャンアプリまたはカメラアプリを使用してください。
              </span>
              <img src={infoIcon} alt='info' className='w-6 h-6 cursor-help' />
            </div>
            <div className='space-x-[9px]'>
              <button
                className='w-[105px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] text-[16px] font-semibold'
                onClick={onCopyUrl}>
                コピー
              </button>
              <button
                className='w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] text-[16px] font-semibold'
                onClick={() => {
                  if (sample.id) {
                    onQrDownload(sample.id)
                  }
                }}
              >
                ダウンロード
              </button>
            </div>
          </div>
          <MessageModal isOpen={isModalOpen} onClose={closeModal} />
        </Modal>
      ) : (
        <Modal isOpen={isOpen} onClose={onClose} className='flex justify-center items-center w-[485px] h-[185px]' >
          <img src={errorIcon} alt='error' className='w-[26px] h-[26px] mr-[12px]' />
          <div className='flex flex-col'>
            {saveResult[1] === 409 ? (
              <>
                <span>同じサンプルが存在しています。</span>
                <span>入力内容を確認してください。</span>
              </>
            ) : (
              <>
                <span>saveできませんでした。</span>
                <span>再度saveボタンをクリックしてください。</span>
              </>
            )}
          </div>
        </Modal>
      )
      }
    </>
  )
}

function MessageModal({ isOpen, onClose }: { isOpen: boolean, onClose: () => void }) {

  return (
    <Modal isOpen={isOpen} onClose={onClose} className='flex items-center justify-center w-[414px] h-[130px]'>
      <img src={checkIcon} alt='check' className='w-6 h-6 mr-[5px]' />
      <span className='text-[14px]'>リンクをコピーしました。</span>
    </Modal>
  )
}

function BlankSampleModal({ isOpen, onClose }: { isOpen: boolean, onClose: () => void }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} className='w-[500px] h-[200px] flex flex-col items-center justify-center'>
      <div className='flex items-center space-x-[5px]'>
        <img src={errorIcon} alt='error' className='w-[26px]' />
        <p>採寸箇所名が未設定のデータがあります。</p>
      </div>
      <button
        className='mt-[25px] w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] font-bold'
        onClick={onClose}
      >
        戻る
      </button>
    </Modal>
  )
}